import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type InvoiceReminder = components["schemas"]["AdminInvoiceReminderResponse"];
type InvoiceReminderStatus = NonNullable<components["schemas"]["InvoiceReminderStatus"]>;
type InvoiceReminderOffset = NonNullable<components["schemas"]["InvoiceReminderOffset"]>;

interface InvoiceRemindersTableProps {
  organisationId?: string;
  invoiceId?: string;
  /** Keep the Invoice and Organisation columns visible even when filtered by them. */
  showAllColumns?: boolean;
}

const statusFilterOptions: { value: InvoiceReminderStatus | "any"; label: string }[] = [
  { value: "any", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Sent", label: "Sent" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Failed", label: "Failed" },
];

const offsetFilterOptions: { value: InvoiceReminderOffset | "any"; label: string }[] = [
  { value: "any", label: "All offsets" },
  { value: "OneWeekBefore", label: "1 week before" },
  { value: "TwoDaysBefore", label: "2 days before" },
  { value: "OnDueDate", label: "On due date" },
  { value: "TwoDaysAfter", label: "2 days after" },
  { value: "OneWeekAfter", label: "1 week after" },
];

export const invoiceReminderOffsetLabels: Record<InvoiceReminderOffset, string> = {
  OneWeekBefore: "1 week before",
  TwoDaysBefore: "2 days before",
  OnDueDate: "On due date",
  TwoDaysAfter: "2 days after",
  OneWeekAfter: "1 week after",
};

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Sent":
      return "default";
    case "Pending":
      return "secondary";
    case "Failed":
      return "destructive";
    case "Cancelled":
      return "outline";
    default:
      return "outline";
  }
}

export function InvoiceRemindersTable({
  organisationId,
  invoiceId,
  showAllColumns = false,
}: InvoiceRemindersTableProps) {
  const [status, setStatus] = useState<InvoiceReminderStatus | "any">("any");
  const [offset, setOffset] = useState<InvoiceReminderOffset | "any">("any");
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [reminders, setReminders] = useState<InvoiceReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevFiltersRef = useRef({ status, offset, organisationId, invoiceId });
  useEffect(() => {
    if (
      prevFiltersRef.current.status !== status ||
      prevFiltersRef.current.offset !== offset ||
      prevFiltersRef.current.organisationId !== organisationId ||
      prevFiltersRef.current.invoiceId !== invoiceId
    ) {
      prevFiltersRef.current = { status, offset, organisationId, invoiceId };
      pagination.reset();
    }
  }, [status, offset, organisationId, invoiceId, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchReminders() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/invoice-reminders", {
        params: {
          query: {
            OrganisationId: organisationId,
            InvoiceId: invoiceId,
            Status: status === "any" ? undefined : status,
            Offset: offset === "any" ? undefined : offset,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load invoice reminders.");
      } else {
        setReminders(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchReminders();
    return () => {
      cancelled = true;
    };
  }, [organisationId, invoiceId, status, offset, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  const showInvoiceColumn = showAllColumns || !invoiceId;
  const showOrganisationColumn = showAllColumns || !organisationId;
  const colSpan = 9 + (showInvoiceColumn ? 1 : 0) + (showOrganisationColumn ? 1 : 0);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={status} onValueChange={(v) => setStatus(v as InvoiceReminderStatus | "any")}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusFilterOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={offset} onValueChange={(v) => setOffset(v as InvoiceReminderOffset | "any")}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {offsetFilterOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              {showInvoiceColumn && <TableHead>Invoice</TableHead>}
              {showOrganisationColumn && <TableHead>Organisation</TableHead>}
              <TableHead>Offset</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Scheduled</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Cancelled</TableHead>
              <TableHead>Failure</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reminders.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-muted-foreground h-24 text-center">
                  No invoice reminders found.
                </TableCell>
              </TableRow>
            ) : (
              reminders.map((reminder) => (
                <TableRow key={reminder.id}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {reminder.id.slice(0, 8)}...
                    </Badge>
                  </TableCell>
                  {showInvoiceColumn && (
                    <TableCell>
                      <Link
                        to={`/invoices/${reminder.invoiceId}?organisationId=${reminder.organisationId}`}
                      >
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {reminder.invoiceId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  {showOrganisationColumn && (
                    <TableCell>
                      <Link to={`/organisations/${reminder.organisationId}`}>
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {reminder.organisationId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell className="text-sm">
                    {reminder.offset ? (
                      invoiceReminderOffsetLabels[reminder.offset]
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(reminder.status)}>{reminder.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm max-w-xs">
                    {reminder.subject ? (
                      <span className="block truncate" title={reminder.subject}>
                        {reminder.subject}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(reminder.scheduledAt)}</TableCell>
                  <TableCell className="text-sm">
                    {reminder.sentAt ? (
                      formatDateTime(reminder.sentAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {reminder.cancelledAt ? (
                      formatDateTime(reminder.cancelledAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {reminder.failureReason ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="destructive" className="text-xs cursor-help">
                            Failed
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          {reminder.failureReason}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(reminder.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
